import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PreAuthFooter } from '../components/PreAuthFooter';

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, name);
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-qc-bg flex flex-col items-center justify-center p-5">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black text-qc-green tracking-tight mb-3">Get Green Light</h1>
          <p className="text-qc-text-dim text-base">Know in seconds. Shop with confidence.</p>
        </div>

        <div className="bg-qc-surface rounded-3xl border border-qc-border p-6">
          <h2 className="text-2xl font-extrabold text-qc-text mb-1">Create your account</h2>
          <p className="text-qc-text-dim text-sm mb-6">Start with 7 free scans</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">
                First Name
              </label>
              <Input
                type="text"
                data-testid="signup-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                className="h-14 rounded-xl bg-qc-surface2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:border-qc-green focus-visible:ring-qc-green text-base"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">
                Email
              </label>
              <Input
                type="email"
                data-testid="signup-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="h-14 rounded-xl bg-qc-surface2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:border-qc-green focus-visible:ring-qc-green text-base"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">
                Password
              </label>
              <Input
                type="password"
                data-testid="signup-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                className="h-14 rounded-xl bg-qc-surface2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:border-qc-green focus-visible:ring-qc-green text-base"
              />
            </div>
            <Button
              type="submit"
              data-testid="signup-submit-button"
              disabled={loading}
              className="w-full h-14 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-xl font-extrabold text-base shadow-[0_8px_24px_rgba(0,230,118,0.25)] mt-2"
            >
              {loading ? 'Creating account...' : 'Get Started'}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-qc-text-dim">
            Already have an account?{' '}
            <Link to="/login" className="text-qc-green font-bold hover:text-qc-green-dark" data-testid="login-link">
              Log in
            </Link>
          </p>
        </div>

        <PreAuthFooter />
      </div>
    </div>
  );
}
